// Runtime configuration loader for frontend
// Loads configuration from /config.json at runtime instead of build-time environment variables

export type AppConfig = {
  // Backend API URLs
  API_BASE_URL: string;
  BACKEND: string;
  BACKEND_URL: string;
  BACKEND2?: string;
  
  // Web/Client identification
  WEB_NAME: string;
  CLIENT_ID: string;
  CLIENT_SECRET: string;
  
  // OAuth configuration
  OAUTH_SERVER_URL: string;
  OAUTH_URL?: string;
  RESOURCE_SERVER_URL: string;
  GRANT_TYPE: string;
  SCOPE: string;
  REDIRECT_URI: string;
  RESPONSE_TYPE?: string;
  
  // File server
  FILE_SERVER_URL: string;

  // TTS API Gateway synthesize URL (key kept for compatibility)
  POLLY_API_URL?: string;
  
  // Debug flag
  DEBUG?: string;
};

let cachedConfig: AppConfig | null = null;
let loadPromise: Promise<AppConfig> | null = null;

function mapJsonToConfig(json: any): AppConfig {
  return {
    API_BASE_URL: json.API_BASE_URL || json.REACT_APP_API_URL || '',
    BACKEND: json.BACKEND || json.REACT_APP_BACKEND || json.API_BASE_URL || json.REACT_APP_API_URL || '',
    BACKEND_URL: json.BACKEND_URL || json.REACT_APP_BACKEND_URL || json.API_BASE_URL || json.REACT_APP_API_URL || '',
    BACKEND2: json.BACKEND2 || json.REACT_APP_BACKEND2,
    WEB_NAME: json.WEB_NAME || json.REACT_APP_WEB_NAME || '',
    CLIENT_ID: json.CLIENT_ID || json.REACT_APP_CLIENT_ID || '',
    CLIENT_SECRET: json.CLIENT_SECRET || json.REACT_APP_CLIENT_SECRET || '',
    OAUTH_SERVER_URL: json.OAUTH_SERVER_URL || json.REACT_APP_OAUTH_SERVER_URL || '',
    OAUTH_URL: json.OAUTH_URL || json.REACT_APP_OAUTH_URL,
    RESOURCE_SERVER_URL: json.RESOURCE_SERVER_URL || json.REACT_APP_RESOURCE_SERVER_URL || '',
    GRANT_TYPE: json.GRANT_TYPE || json.REACT_APP_GRANT_TYPE || 'password',
    SCOPE: json.SCOPE || json.REACT_APP_SCOPE || 'app',
    REDIRECT_URI: json.REDIRECT_URI || json.REACT_APP_REDIRECT_URI || '',
    RESPONSE_TYPE: json.RESPONSE_TYPE || json.REACT_APP_RESPONSE_TYPE,
    FILE_SERVER_URL: json.FILE_SERVER_URL || json.REACT_APP_FILE_SERVER_URL || '',
    POLLY_API_URL: json.POLLY_API_URL || json.REACT_APP_POLLY_API_URL || '',
    DEBUG: json.DEBUG || json.REACT_APP_DEBUG,
  };
}

/**
 * Load configuration from /config.json
 * This should be called once before the app starts
 */
export async function loadConfig(): Promise<AppConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }
  
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      const res = await fetch('/config.json', { 
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!res.ok) {
        // Fallback to process.env in development mode
        if (process.env.NODE_ENV === 'development') {
          console.warn('config.json not found, falling back to process.env');
          cachedConfig = mapJsonToConfig(process.env);
          return cachedConfig;
        }
        throw new Error(`Failed to load config.json: ${res.status} ${res.statusText}`);
      }
      
      const json = await res.json();
      cachedConfig = mapJsonToConfig(json);
      return cachedConfig;
    } catch (error) {
      // Fallback to process.env in development mode
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to load config.json, falling back to process.env:', error);
        cachedConfig = mapJsonToConfig(process.env);
        return cachedConfig;
      }
      console.error('Failed to load config.json:', error);
      throw error;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * Get the cached configuration
 * Must call loadConfig() first
 */
export function getConfig(): AppConfig {
  if (cachedConfig) {
    return cachedConfig;
  }
  
  // Synchronous fallback for development mode
  if (process.env.NODE_ENV === 'development') {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/config.json', false); // Synchronous request
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      xhr.send(null);
      
      if (xhr.status === 200) {
        const json = JSON.parse(xhr.responseText);
        cachedConfig = mapJsonToConfig(json);
        console.warn('Config loaded synchronously as fallback. Consider ensuring loadConfig() is called before getConfig().');
        return cachedConfig;
      }
    } catch (syncError) {
      console.error('Synchronous config load failed:', syncError);
      // Fallback to process.env
      cachedConfig = mapJsonToConfig(process.env);
      return cachedConfig;
    }
  }
  
  throw new Error('Config not loaded yet. Call loadConfig() first.');
}

/**
 * Check if config is loaded
 */
export function isConfigLoaded(): boolean {
  return cachedConfig !== null;
}

